import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { useHistory } from 'react-router-dom';

import {
  useAllFakturowniaInvoices,
  useExpenseAggregates,
  useFakturowniaInvoiceAggregates,
  useFakturowniaSync,
} from 'core/hooks';
import { PATHS } from 'router/config';
import { KpiCard, KpiGrid } from 'molecules';

interface IDashboardKpisProps {
  dateFrom: string;
  dateTo: string;
}

const fmt = (n: number) => n.toFixed(2);

/**
 * Symmetric range of the same length as the current selection, ending the
 * day before `dateFrom`. Lets the KPI cards show a «↑ +12% vs previous
 * period» pill without the user having to compare numbers themselves.
 */
const previousRange = (
  dateFrom: string,
  dateTo: string,
): { from: string; to: string } | null => {
  if (!dateFrom || !dateTo) return null;
  const from = moment(dateFrom);
  const to = moment(dateTo);
  const lengthDays = to.diff(from, 'days');
  if (lengthDays < 0) return null;
  const prevTo = from.clone().subtract(1, 'day');
  const prevFrom = prevTo.clone().subtract(lengthDays, 'days');
  return {
    from: prevFrom.format('YYYY-MM-DD'),
    to: prevTo.format('YYYY-MM-DD'),
  };
};

const pctChange = (current: number, previous: number): number => {
  if (previous === 0) return current === 0 ? 0 : 100;
  return ((current - previous) / Math.abs(previous)) * 100;
};

/**
 * Top KPI strip — five cards: «Issued / Received / Expected / Expenses /
 * Unmatched». Phase 4: native (non-Byn) aggregate fields, labelled PLN.
 * Phase 6: each money card also carries a «vs previous period» delta —
 * dual-call to the same aggregate query with the prior-window dates.
 */
export const DashboardKpis = memo(
  ({ dateFrom, dateTo }: IDashboardKpisProps) => {
    const { t } = useTranslation();
    const history = useHistory();

    const prev = useMemo(() => previousRange(dateFrom, dateTo), [
      dateFrom,
      dateTo,
    ]);

    const { rows: invoiceAgg } = useFakturowniaInvoiceAggregates({
      direction: 'issued',
      dateFrom,
      dateTo,
      groupBy: 'none',
    });
    const { rows: invoiceAggPrev } = useFakturowniaInvoiceAggregates({
      direction: 'issued',
      dateFrom: prev?.from,
      dateTo: prev?.to,
      groupBy: 'none',
      skip: !prev,
    });
    const { rows: monthBuckets } = useExpenseAggregates(
      dateFrom,
      dateTo,
      'month',
    );
    const { rows: monthBucketsPrev } = useExpenseAggregates(
      prev?.from || '',
      prev?.to || '',
      'month',
      { skip: !prev },
    );
    // ТЗ 0004: the reconciliation queue = issued, linkable invoices that
    // aren't pinned to a взнос yet (`needsLinkOnly` — `contribution IS
    // NULL`). Mirrors the «Очередь сверки» chip on the Invoices page; the
    // legacy payment-less `unpaidOnly` filter is gone.
    const { data: reconcileRows } = useAllFakturowniaInvoices({
      direction: 'issued',
      needsLinkOnly: true,
      perPage: 100,
    });
    // Touch the sync hook so polling refreshes invoices in the background too.
    useFakturowniaSync();

    const agg = invoiceAgg[0];
    const aggPrev = invoiceAggPrev[0];
    const issuedPln = agg?.totalGross ?? 0;
    const receivedPln = agg?.totalPaid ?? 0;
    const expectedPln = agg?.totalOutstanding ?? 0;
    const issuedPlnPrev = aggPrev?.totalGross ?? 0;
    const receivedPlnPrev = aggPrev?.totalPaid ?? 0;
    const expensesPln = monthBuckets.reduce((s, b) => s + (b.total || 0), 0);
    const expensesPlnPrev = monthBucketsPrev.reduce(
      (s, b) => s + (b.total || 0),
      0,
    );
    const reconcileCount = reconcileRows.length;

    const deltaLabel = (pct: number) =>
      t('pages.financeDashboard.kpis.deltaVsPrev', { pct: pct.toFixed(1) });

    const mkDelta = (cur: number, prevVal: number, inverse?: boolean) =>
      prev
        ? {
            value: cur - prevVal,
            label: deltaLabel(pctChange(cur, prevVal)),
            inverse,
          }
        : undefined;

    return (
      <KpiGrid columns={5}>
        <KpiCard
          label={t('pages.financeDashboard.kpis.issued')}
          value={`${fmt(issuedPln)} PLN`}
          subLabel={t('pages.financeDashboard.kpis.issuedHint')}
          delta={mkDelta(issuedPln, issuedPlnPrev)}
        />
        <KpiCard
          label={t('pages.financeDashboard.kpis.received')}
          value={`${fmt(receivedPln)} PLN`}
          subLabel={t('pages.financeDashboard.kpis.receivedHint')}
          accent="#16a34a"
          delta={mkDelta(receivedPln, receivedPlnPrev)}
        />
        <KpiCard
          label={t('pages.financeDashboard.kpis.expected')}
          value={`${fmt(expectedPln)} PLN`}
          subLabel={t('pages.financeDashboard.kpis.expectedHint')}
          accent={expectedPln > 0 ? '#d97706' : undefined}
        />
        <KpiCard
          label={t('pages.financeDashboard.kpis.expenses')}
          value={`${fmt(expensesPln)} PLN`}
          subLabel={t('pages.financeDashboard.kpis.fromExpenses')}
          delta={mkDelta(expensesPln, expensesPlnPrev, true)}
        />
        <KpiCard
          label={t('pages.financeDashboard.kpis.reconcile')}
          subLabel={t('pages.financeDashboard.kpis.reconcileHint')}
          value={reconcileCount}
          accent={reconcileCount > 0 ? '#f97316' : undefined}
          onClick={() => history.push(PATHS.financesInvoices)}
        />
      </KpiGrid>
    );
  },
);
